/**
 * 폼 드롭다운용 lookup API — backend /lookup/*.
 */

import type { ApiResponse, CompanyId } from './types';

const BASE = '/api';

export interface EmployeeLookup {
  empId: string;
  name: string;
  title: string | null;
  department: string | null;
}

export interface CompanyLookup {
  companyId: CompanyId;
  companyName: string;
}

export async function listEmployees(): Promise<EmployeeLookup[]> {
  const res = await fetch(`${BASE}/lookup/employees`, { credentials: 'include' });
  const json = (await res.json()) as ApiResponse<EmployeeLookup[]>;
  if (!json.ok) throw new Error(json.error.message);
  return json.data;
}

export async function listCompanies(): Promise<CompanyLookup[]> {
  const res = await fetch(`${BASE}/lookup/companies`, { credentials: 'include' });
  const json = (await res.json()) as ApiResponse<CompanyLookup[]>;
  if (!json.ok) throw new Error(json.error.message);
  return json.data;
}

/** 영업 모달 자동완성용 — 계산서 발행업체 / 제안업체 / 엔드유저 기존 입력값 목록 (빈도 내림차순). */
export interface SalesPartiesLookup {
  invoiceCompanies: string[];
  proposalCompanies: string[];
  endUsers: string[];
}

export async function listSalesParties(): Promise<SalesPartiesLookup> {
  const res = await fetch(`${BASE}/lookup/sales-parties`, { credentials: 'include' });
  const json = (await res.json()) as ApiResponse<SalesPartiesLookup>;
  if (!json.ok) throw new Error(json.error.message);
  return json.data;
}

/** 거래처 마스터(표준 목록) 이름+유형 — 회사명 자동완성 공용 후보. */
export type PartnerKind = 'supplier' | 'customer' | 'both';
export interface PartnerOption {
  name: string;
  kind: PartnerKind;
}

export async function listPartnerCompanies(): Promise<PartnerOption[]> {
  const res = await fetch(`${BASE}/lookup/partner-companies`, { credentials: 'include' });
  const json = (await res.json()) as ApiResponse<PartnerOption[]>;
  if (!json.ok) throw new Error(json.error.message);
  return json.data;
}

/**
 * 거래처 후보를 칸 성격에 맞게 이름 목록으로 추출.
 *   'supplier' → 매입처 + 양쪽 / 'customer' → 고객사 + 양쪽 / 미지정 → 전체
 */
export function partnerNames(
  opts: PartnerOption[] | undefined,
  filter?: 'supplier' | 'customer',
): string[] {
  if (!opts) return [];
  if (!filter) return opts.map((o) => o.name);
  return opts.filter((o) => o.kind === 'both' || o.kind === filter).map((o) => o.name);
}

/**
 * 영업건 「계산서 발행업체」 후보 (v2.306.0) — 우리가 매출 계산서를 끊어 주는 곳이라 **거래처 전부**.
 *   총판·SI(매입처·양쪽)가 대부분이라 그쪽을 먼저, 고객사 전용(직접 발행하는 기관·회사)을 뒤에.
 *   ⚠예전엔 'supplier' 만 줘서 고객사로만 등록한 곳(운영 208곳)이 검색에 안 나왔다 —
 *     챗봇 대화 #38(10-22 「픽센」)에서 직원이 거래처 유형을 「양쪽」으로 바꿔 우회했다.
 */
export function invoiceCompanyNames(opts: PartnerOption[] | undefined): string[] {
  if (!opts) return [];
  return [
    ...opts.filter((o) => o.kind !== 'customer').map((o) => o.name),
    ...opts.filter((o) => o.kind === 'customer').map((o) => o.name),
  ];
}
